import dayjs from 'dayjs';
import { useUserStore } from '@/store';
import { issueApi } from '@/api/issue';
import { issueProjectApi } from '@/api/issue/project';
import { issueStaticsApi } from '@/api/issue/issue-statics';
import { issueVersionsApi } from '@/api/issue/issue-versions';
import { projectApi } from '@/api/common/project';
import { testManagementApi } from '@/api/production/test-management';
import { iqcInspectReceiveBillApi } from '@/api/production/iqc-inspect-receive-bill';
import { wmsReceiveBillApi } from '@/api/production/wms-receive-bill';
import { saveWorkItem } from '@/api/ai/work-item';
import { matchProject, resolvePriorityIds, resolveStatusIds, type ToolExecutor } from './tools';

/**
 * 工具执行器 + 白名单注册表。每个执行器包装一个 CTC 已有的【只读】接口，
 * 用用户 token 调用，权限由被包装接口自身判定（前端不额外放权）。
 * 与 tools.ts 分离，隔离 @/utils/http 的 Vite 专有依赖，保持 tools.ts 可单测。
 */

/**
 * 把可选项目名解析成 count 接口的 filter。空名 → {}（全部有权限项目）；
 * 找不到项目名 → 抛出（由 executeToolCall 兜成 error 字符串回填给模型）。
 */
async function resolveProjectFilter(projectName?: string): Promise<Record<string, any>> {
  const name = (projectName ?? '').toString().trim();
  if (!name) {
    return {};
  }
  const projResp = await issueProjectApi.getAllProjects();
  if (projResp.code !== 0) {
    throw new Error(`获取项目列表失败：${projResp.msg}`);
  }
  const m = matchProject(projResp.data ?? [], name);
  if ('id' in m) {
    return { project: [m.id] };
  }
  if ('ambiguous' in m) {
    throw new Error(
      `项目名「${name}」不唯一，匹配到：${m.ambiguous.slice(0, 8).join('、')}${m.ambiguous.length > 8 ? ' 等' : ''}，请给更精确的名称`,
    );
  }
  throw new Error(`未找到名为「${name}」的项目，请确认项目名或改为不指定项目`);
}

const scopeLabel = (projectName?: string) => (projectName ?? '').toString().trim() || '全部有权限的项目';

/** 拼缺陷详情页可点链接（hash 路由，key = pkey-issueNum，如 MBLOCK-5306）。 */
const issueLink = (key: string) => `${window.location.origin}/#/issue/detail/${key}`;

/**
 * 来料/收料单 date 参数：YYYY-MM-DD 起止 → [起始当天 00:00, 结束当天 23:59:59] 的 **unix 秒级时间戳**。
 * dev 真机核实：receiveBill/list、wmsReceiveBill/list 的 date 认秒级 ts（毫秒/字符串都不认，静默返回空）。
 * 起止只给一个时也补另一端（2000/2099），返回 undefined 表示不筛。
 */
function toUnixDateRange(from?: any, to?: any): [number, number] | undefined {
  const f = (from ?? '').toString().trim();
  const t = (to ?? '').toString().trim();
  if (!f && !t) {
    return undefined;
  }
  const start = dayjs(f || '2000-01-01')
    .startOf('day')
    .unix();
  const end = dayjs(t || '2099-12-31')
    .endOf('day')
    .unix();
  return [start, end];
}

/**
 * 由 args 组装缺陷筛选 filter（query_issues / query_issue_stats 共用）。
 * 名→id 映射：项目=matchProject、状态/优先级=稳定字典、时间=日期字符串、mine=当前用户 id。
 * ⚠️ reporter/assignee 后端为 []int64（真机核实，前端 TS 类型 string[] 有误），传数字数组。
 */
async function buildIssueFilter(args: any): Promise<Record<string, any>> {
  const f: Record<string, any> = await resolveProjectFilter(args?.projectName);
  const st = resolveStatusIds(args?.status);
  if (st.length) {
    f.issueStatus = st;
  }
  const pr = resolvePriorityIds(args?.priority);
  if (pr.length) {
    f.priority = pr;
  }
  const from = (args?.createdFrom ?? '').toString().trim();
  const to = (args?.createdTo ?? '').toString().trim();
  if (from || to) {
    f.createdAt = [from || '2000-01-01', to || '2099-12-31'];
  }
  const kw = (args?.keyword ?? '').toString().trim();
  if (kw) {
    f.summary = kw;
  }
  if (args?.mine === 'reporter' || args?.mine === 'assignee') {
    const uid = useUserStore().current?.id;
    if (typeof uid === 'number') {
      f[args.mine] = [uid];
    }
  }
  const versionName = (args?.version ?? '').toString().trim();
  if (versionName) {
    f.versions = await resolveVersionIds(f.project, versionName);
  }
  return f;
}

/**
 * 版本名 → 版本 id：版本隶属项目，故必须已解析出 project 才能查版本表（issueVersionsApi，mfd）。
 * 用 matchProject 复用同一套忽略大小写/子串唯一命中逻辑（版本名走 vname 字段）。
 * 找不到/不唯一/缺项目都抛出，由 executeToolCall 兜成 error 字符串回填模型。
 */
async function resolveVersionIds(project: any, versionName: string): Promise<number[]> {
  const projIds: number[] = Array.isArray(project) ? project : [];
  if (projIds.length !== 1) {
    throw new Error('按版本筛请同时指定单个项目名（版本隶属于具体项目，需先确定项目）');
  }
  const resp = await issueVersionsApi.getList({ project: projIds });
  if (resp.code !== 0) {
    throw new Error(`获取项目版本列表失败：${resp.msg}`);
  }
  const versions = (resp.data ?? []).map((v: any) => ({ id: v.id, name: v.vname ?? '' }));
  const m = matchProject(versions, versionName);
  if ('id' in m) {
    return [m.id];
  }
  if ('ambiguous' in m) {
    throw new Error(
      `版本名「${versionName}」不唯一，匹配到：${m.ambiguous.slice(0, 8).join('、')}${m.ambiguous.length > 8 ? ' 等' : ''}，请给更精确的版本名`,
    );
  }
  throw new Error(`该项目下未找到版本「${versionName}」，请确认版本名`);
}

/**
 * 通用缺陷查询：组合筛选 → 计数(count/base) 或 清单(getList)。
 * count 用 count/base（有 total/closed/suspended，真机核实认 issueStatus 筛选）；
 * list 用 getList（认全部筛选，但无 total，故只返回当页清单）。只读。
 */
const queryIssues: ToolExecutor = async (args) => {
  const filter = await buildIssueFilter(args);
  const mode = args?.mode === 'list' ? 'list' : 'count';

  if (mode === 'count') {
    const resp = await issueStaticsApi.getBase(filter);
    if (resp.code !== 0) {
      return JSON.stringify({ error: `查询失败：${resp.msg}` });
    }
    const d = resp.data ?? {};
    const total = d.total ?? 0;
    const closed = d.closed ?? 0;
    const suspended = d.suspended ?? 0;
    const refused = d.refused ?? 0;
    return JSON.stringify({
      mode: 'count',
      scope: scopeLabel(args?.projectName),
      total,
      closed,
      suspended,
      refused,
      unclosed: total - closed - suspended - refused,
    });
  }

  const pageSize = 20;
  const resp = await issueApi.getList({ pageIndex: 1, pageSize, ...filter } as any);
  if (resp.code !== 0) {
    return JSON.stringify({ error: `查询失败：${resp.msg}` });
  }
  const list = resp.data?.list ?? [];
  const issues = list.map((it: any) => ({
    key: `${it.pkey}-${it.issueNum}`,
    summary: it.summary,
    status: it.issueStatusName,
    priority: it.priorityName,
    link: issueLink(`${it.pkey}-${it.issueNum}`),
  }));
  return JSON.stringify({ mode: 'list', returned: issues.length, truncated: issues.length >= pageSize, issues });
};

/** severityData 各行按严重度求和 → 扁平严重度分布（真机核实字段：fatal/serious/common/tips/suggest）。 */
function aggregateSeverity(rows: any[]) {
  const levels = [
    { key: 'fatalCount', name: '致命' },
    { key: 'seriousCount', name: '严重' },
    { key: 'commonCount', name: '一般' },
    { key: 'tipsCount', name: '提示' },
    { key: 'suggestCount', name: '建议' },
  ];
  return levels.map((lv) => ({
    name: lv.name,
    count: rows.reduce((s: number, r: any) => s + (r[lv.key] ?? 0), 0),
  }));
}

/**
 * 缺陷分布工具执行器：按状态/优先级/严重程度统计（issueStaticsApi，只读）。
 * severity 走 getSeverityData（“归属分类 × 严重度”矩阵，本工具按严重度求和成扁平分布）。
 * 优先级(priority)与严重程度(severity)是不同字段，各走各端点，不混用。
 */
const queryIssueStats: ToolExecutor = async (args) => {
  const filter = await buildIssueFilter(args);
  const raw = (args?.dimension ?? 'status').toString().trim();
  const dimension = ['priority', 'severity', 'assignee', 'dept'].includes(raw) ? raw : 'status';
  const scope = scopeLabel(args?.projectName);

  if (dimension === 'severity') {
    const resp = await issueStaticsApi.getSeverityData(filter);
    if (resp.code !== 0) {
      return JSON.stringify({ error: `查询缺陷严重度分布失败：${resp.msg}` });
    }
    return JSON.stringify({ scope, dimension, distribution: aggregateSeverity(resp.data ?? []) });
  }

  // 经办人/部门：返回 名称 + 总数 + 未关闭(新创建+处理中+已解决)，供"团队负载"用
  if (dimension === 'assignee' || dimension === 'dept') {
    const resp =
      dimension === 'assignee'
        ? await issueStaticsApi.getAssigneeData(filter)
        : await issueStaticsApi.getTotalDataByDept(filter);
    if (resp.code !== 0) {
      return JSON.stringify({ error: `查询缺陷分布失败：${resp.msg}` });
    }
    const distribution = (resp.data ?? []).map((r: any) => ({
      name: dimension === 'assignee' ? r.assigneeName : r.belongsName,
      count: r.count ?? 0,
      unclosed: (r.createdCount ?? 0) + (r.processedCount ?? 0) + (r.solvedCount ?? 0),
    }));
    return JSON.stringify({ scope, dimension, distribution });
  }

  const resp =
    dimension === 'priority' ? await issueStaticsApi.getPriority(filter) : await issueStaticsApi.getStatus(filter);
  if (resp.code !== 0) {
    return JSON.stringify({ error: `查询缺陷分布失败：${resp.msg}` });
  }
  const rows = resp.data ?? [];
  const distribution = rows.map((r: any) => ({
    name: dimension === 'priority' ? r.priorityName : r.issueStatusName,
    count: r.count ?? 0,
  }));
  return JSON.stringify({ scope, dimension, distribution });
};

/**
 * 缺陷趋势执行器：包装 issueStaticsApi.getBugTrend（只读）。
 * 返回各时间桶的新增/关闭/重开数（dev 真机核实字段：createCount/closedCount/reOpenCount）。
 * 复用 buildIssueFilter（项目/版本/时间等筛选），额外传 timeDimension 控制粒度。
 */
const queryIssueTrend: ToolExecutor = async (args) => {
  const filter = await buildIssueFilter(args);
  const raw = (args?.timeDimension ?? 'month').toString().trim();
  const timeDimension = ['day', 'week', 'month'].includes(raw) ? raw : 'month';
  const resp = await issueStaticsApi.getBugTrend({ ...filter, timeDimension });
  if (resp.code !== 0) {
    return JSON.stringify({ error: `查询缺陷趋势失败：${resp.msg}` });
  }
  const trend = (resp.data ?? []).map((r: any) => ({
    date: r.date,
    created: r.createCount ?? 0,
    closed: r.closedCount ?? 0,
    reopened: r.reOpenCount ?? 0,
  }));
  return JSON.stringify({ scope: scopeLabel(args?.projectName), timeDimension, trend });
};

/**
 * 测试计划执行进度执行器（用例维度，只读）。链路：
 *  研发项目名 → sysProjectId（projectApi.getUserSysProject，权限内项目，真机返回 {id,name}）
 *  → testManagementApi.planList({sysProjectId}) 拿计划
 *  → 每个计划 GetPlanStatistics(id) 拿用例统计 {total,prepare,pass,fail,na,block}。
 * executed=total-prepare；执行率=executed/total；通过率=pass/executed。为控调用量，最多算 MAX_PLANS 个计划。
 */
const MAX_PLANS = 15;
const queryTestPlanProgress: ToolExecutor = async (args) => {
  const name = (args?.projectName ?? '').toString().trim();
  if (!name) {
    return JSON.stringify({ error: '请提供研发项目名 projectName' });
  }
  const projResp = await projectApi.getUserSysProject();
  if (projResp.code !== 0) {
    return JSON.stringify({ error: `获取研发项目列表失败：${projResp.msg}` });
  }
  // 真机核实：实际字段是 {id,name}（TS 类型声明的 projectId/projectName 与后端不符），两者都兜底
  const projects = (projResp.data ?? []).map((p: any) => ({ id: p.id ?? p.projectId, name: p.name ?? p.projectName }));
  const m = matchProject(projects, name);
  if ('ambiguous' in m) {
    return JSON.stringify({ error: `研发项目名「${name}」不唯一：${m.ambiguous.slice(0, 8).join('、')}，请更精确` });
  }
  if ('notFound' in m) {
    return JSON.stringify({ error: `未找到研发项目「${name}」，请确认项目名` });
  }
  const listResp = await testManagementApi.planList({ sysProjectId: m.id, pageIndex: 1, pageSize: 50 });
  if (listResp.code !== 0) {
    return JSON.stringify({ error: `获取测试计划列表失败：${listResp.msg}` });
  }
  const raw = (listResp.data as any)?.list ?? (Array.isArray(listResp.data) ? listResp.data : []);
  const planName = (args?.planName ?? '').toString().trim().toLowerCase();
  let plans = planName ? raw.filter((p: any) => (p.name ?? '').toString().toLowerCase().includes(planName)) : raw;
  const truncated = plans.length > MAX_PLANS;
  plans = plans.slice(0, MAX_PLANS);
  const progress = await Promise.all(
    plans.map(async (p: any) => {
      const st = await testManagementApi.GetPlanStatistics(p.id);
      const s = st.code === 0 ? (st.data ?? {}) : {};
      const total = s.total ?? 0;
      const prepare = s.prepare ?? 0;
      const executed = Math.max(0, total - prepare);
      const rate = (n: number, d: number) => (d > 0 ? Math.round((n / d) * 1000) / 10 : 0);
      return {
        plan: p.name,
        status: p.status,
        stage: p.stage,
        version: p.projectVersionName ?? null,
        total,
        executed,
        untested: prepare,
        pass: s.pass ?? 0,
        fail: s.fail ?? 0,
        block: s.block ?? 0,
        na: s.na ?? 0,
        executedRate: `${rate(executed, total)}%`,
        passRate: `${rate(s.pass ?? 0, executed)}%`,
      };
    }),
  );
  return JSON.stringify({ project: name, planCount: progress.length, truncated, plans: progress });
};

/** IQC 来料检验单查询执行器：包装 iqcInspectReceiveBillApi.list（只读）。 */
const queryIqcReceiveBills: ToolExecutor = async (args) => {
  const params: Record<string, any> = {};
  for (const k of ['materialName', 'materialNumber', 'status', 'billNo']) {
    const v = (args?.[k] ?? '').toString().trim();
    if (v) {
      params[k] = v;
    }
  }
  const date = toUnixDateRange(args?.createdFrom, args?.createdTo);
  if (date) {
    params.date = date;
  }
  const resp = await iqcInspectReceiveBillApi.list(params);
  if (resp.code !== 0) {
    return JSON.stringify({ error: `查询来料检验单失败：${resp.msg}` });
  }
  const d = resp.data ?? ({} as any);
  const list = d.list ?? [];
  // 总数字段：receiveBill/list 实际返回 total（经 dev 真机核实，非类型声明的 count），兜底 list 长度
  // 只返回总数 + 前 3 条原始条目（不臆测条目字段名，模型自行读取）
  return JSON.stringify({ total: d.total ?? d.count ?? list.length, sample: list.slice(0, 3) });
};

/** 缺陷详情工具执行器：按 issueKey 查单个缺陷（issueApi.getByKey，只读）。 */
const getIssueDetail: ToolExecutor = async (args) => {
  const key = (args?.issueKey ?? '').toString().trim();
  if (!key) {
    return JSON.stringify({ error: '请提供缺陷编号 issueKey（如 CTC-123）' });
  }
  const resp = await issueApi.getByKey(key);
  if (resp.code !== 0) {
    return JSON.stringify({ error: `查询缺陷「${key}」失败：${resp.msg}` });
  }
  if (!resp.data) {
    return JSON.stringify({ error: `未找到缺陷「${key}」或无权查看` });
  }
  // 返回原始详情（单个对象，不臆测字段名，模型自行读取）+ 详情页链接
  return JSON.stringify({ link: issueLink(key), issue: resp.data });
};

/** WMS 收料单查询执行器：包装 wmsReceiveBillApi.list（只读）。 */
const queryWmsReceiveBills: ToolExecutor = async (args) => {
  const params: Record<string, any> = {};
  for (const k of ['materialName', 'materialNumber', 'status', 'billNo']) {
    const v = (args?.[k] ?? '').toString().trim();
    if (v) {
      params[k] = v;
    }
  }
  const date = toUnixDateRange(args?.createdFrom, args?.createdTo);
  if (date) {
    params.date = date;
  }
  const resp = await wmsReceiveBillApi.list(params);
  if (resp.code !== 0) {
    return JSON.stringify({ error: `查询 WMS 收料单失败：${resp.msg}` });
  }
  const d = resp.data ?? ({} as any);
  const list = d.list ?? [];
  // 总数字段与 IQC 一致：优先 total，兜底 count / list 长度（真机核实）
  return JSON.stringify({ total: d.total ?? d.count ?? list.length, sample: list.slice(0, 3) });
};

/**
 * 缺陷查重执行器：按关键词搜标题/描述含该词的缺陷，返回候选清单（供 LLM 判重）。
 * 用 getList 的 summary 参数（dev 真机核实为标题/描述关键词检索）。只读。
 */
const searchSimilarIssues: ToolExecutor = async (args) => {
  const keyword = (args?.keyword ?? '').toString().trim();
  if (!keyword) {
    return JSON.stringify({ error: '请提供一个查重关键词' });
  }
  const filter = await resolveProjectFilter(args?.projectName);
  const body: Record<string, any> = { pageIndex: 1, pageSize: 15, summary: keyword, ...filter };
  const resp = await issueApi.getList(body as any);
  if (resp.code !== 0) {
    return JSON.stringify({ error: `查重搜索失败：${resp.msg}` });
  }
  const list = resp.data?.list ?? [];
  const candidates = list.map((it: any) => ({
    key: `${it.pkey}-${it.issueNum}`,
    summary: it.summary,
    status: it.issueStatusName,
    link: issueLink(`${it.pkey}-${it.issueNum}`),
  }));
  return JSON.stringify({ keyword, matched: candidates.length, candidates });
};

/**
 * 提交工作项入库执行器——**唯一的写工具**。把已聊清的需求/bug 落库进管理员审核队列。
 * 权限由后端按 JWT 判定（任意登录用户可提交），前端不额外放开。系统提示词要求
 * 模型在【征得用户明确同意后】才调用（见 requirement-assistant.ts 提交入库流程）。
 */
const saveWorkItemExec: ToolExecutor = async (args, ctx) => {
  const type = (args?.type ?? '').toString().trim();
  if (type !== 'bug' && type !== 'feature') {
    return JSON.stringify({ error: 'type 必须是 bug 或 feature' });
  }
  const title = (args?.title ?? '').toString().trim();
  if (!title) {
    return JSON.stringify({ error: '缺少 title（简洁标题）' });
  }
  const spec = args?.spec && typeof args.spec === 'object' ? args.spec : {};
  const resp = await saveWorkItem({
    rawText: (args?.rawText ?? '').toString(),
    type: type as 'bug' | 'feature',
    title,
    spec,
    // 带上当前会话 ID，让审核页可反查这条工单诞生的原始对话（含图片）
    conversationId: ctx?.conversationId,
  });
  if (resp.code !== 0) {
    return JSON.stringify({ error: `保存失败：${resp.msg}` });
  }
  const id = (resp.data as any)?.id;
  return JSON.stringify({ saved: true, id, message: `已记录（编号 ${id}），进入待审核队列` });
};

/** 工具白名单：模型只能调用这里注册的工具。 */
export const TOOL_REGISTRY: Record<string, ToolExecutor> = {
  query_issues: queryIssues,
  query_issue_stats: queryIssueStats,
  query_issue_trend: queryIssueTrend,
  query_test_plan_progress: queryTestPlanProgress,
  get_issue_detail: getIssueDetail,
  search_similar_issues: searchSimilarIssues,
  query_iqc_receive_bills: queryIqcReceiveBills,
  query_wms_receive_bills: queryWmsReceiveBills,
  save_work_item: saveWorkItemExec,
};
